"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Calendar, ThermometerSnowflake, SunMedium, CircleCheck as CheckCircle2, CircleX as XCircle, Phone, ArrowRight } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
const seasonalCycles = [{
  season: "Autumn & Winter",
  period: "May to September",
  icon: "winter",
  headline: "Rodents enter roof voids and subfloors",
  description: "Auckland's mild, rain-heavy winters never drop cold enough to reduce rodent populations naturally. Instead, rats and mice look for dry, heated refuge inside wall cavities, ceiling insulation, and underfloor spaces, chewing wiring and contaminating insulation.",
  threats: "Roof rats, house mice, and moisture-seeking silverfish"
}, {
  season: "Spring & Summer",
  period: "October to April",
  icon: "summer",
  headline: "Humidity accelerates breeding outbreaks",
  description: "Our coastal subtropical humidity creates rapid breeding conditions. What starts as a small cluster behind kitchen skirting boards or outdoor decking multiplies within weeks into sprawling ant trails, German cockroach nests, and active wasp hives under eaves.",
  threats: "Argentine ants, German cockroaches, flies, and paper wasps"
}];
const comparisonPoints = [{
  approach: "Supermarket Sprays & Foggers",
  isRecommended: false,
  subtitle: "Temporary surface irritant",
  points: ["Kills only visible insects on contact without eliminating the nesting colony", "Pushes cockroaches and ants deeper into internal wall cavities and framing", "Leaves airborne chemical residues in family living rooms and kitchens", "Requires repeat purchases every 2 to 3 weeks with no lasting barrier"]
}, {
  approach: "Eco Home Professional Barrier",
  isRecommended: true,
  subtitle: "Targeted eradication & long-term perimeter defense",
  points: ["Micro-encapsulated treatments that foraging pests carry back into the primary nest", "External perimeter barrier stopping insects before they cross your thresholds", "Targeted subfloor, roof void, and cavity dusting safe around children and pets", "Documented inspections with transparent warranties and follow-up checks"]
}];
export default function Locality() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.42,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="why-it-matters-locally" data-nav="light" className="relative bg-background py-14 md:py-20 text-foreground">
      <div className="container mx-auto max-w-7xl px-5 sm:px-8">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }} className="max-w-3xl mb-12 md:mb-16">
          {/* Pristine Datum Accent */}
          <div className="h-0.5 w-9 bg-primary mb-4" />
          
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs sm:text-sm font-semibold tracking-wider text-secondary-foreground uppercase bg-secondary px-3 py-1 rounded-full border border-border">
              Auckland Climate & Pest Cycles
            </span>
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground leading-tight mb-4">
            Why Auckland homes face year-round pest pressure
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Our coastal geography, humid summers, and temperate winters mean pest cycles in Auckland never truly shut down. Understanding how seasons drive pests indoors helps you protect your home before minor sightings turn into structural contamination.
          </p>
        </motion.div>

        {/* Seasonal Cycles Split */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-16">
          {seasonalCycles.map((cycle, i) => <motion.div key={cycle.season} variants={itemVariants} data-index={i} className="h-full">
              <Card className="h-full border border-border bg-card rounded-lg p-6 sm:p-8 flex flex-col justify-between transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-sm">
                <div>
                  <div className="flex items-center justify-between gap-4 mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-md bg-secondary text-primary flex items-center justify-center shrink-0">
                        {cycle.icon === "winter" ? <ThermometerSnowflake className="w-5 h-5 text-primary" /> : <SunMedium className="w-5 h-5 text-primary" />}
                      </div>
                      <div>
                        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block">
                          {cycle.season}
                        </span>
                        <span className="text-xs font-medium text-foreground">
                          {cycle.period}
                        </span>
                      </div>
                    </div>
                    <span className="text-xs px-2.5 py-1 rounded-full bg-muted text-muted-foreground border border-border">
                      Local Climate Cycle
                    </span>
                  </div>

                  <h3 className="text-lg sm:text-xl font-bold text-foreground mb-3 leading-snug">
                    {cycle.headline}
                  </h3>

                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">
                    {cycle.description}
                  </p>
                </div>

                <div className="pt-4 border-t border-border">
                  <div className="flex items-start gap-2 text-xs sm:text-sm text-foreground">
                    <SafeHydrate><Calendar className="w-4 h-4 text-primary shrink-0 mt-0.5" /></SafeHydrate>
                    <span>
                      <strong className="font-semibold text-foreground">High-risk species: </strong>
                      <span className="text-muted-foreground">{cycle.threats}</span>
                    </span>
                  </div>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Supermarket vs Professional Barrier Comparison */}
        <div className="mb-14">
          <div className="max-w-2xl mb-8">
            <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-2">
              Supermarket aerosol cans versus targeted perimeter barriers
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground">
              Off-the-shelf bug bombs and retail sprays scatter insects into hidden cavities. A professional application treats the perimeter and nest biology directly.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
            {comparisonPoints.map((item, i) => <div key={item.approach} data-index={i} className={`rounded-lg border p-6 sm:p-8 flex flex-col justify-between ${item.isRecommended ? "bg-secondary/40 border-primary/40 shadow-xs" : "bg-muted border-border"}`}>
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2.5">
                      {item.isRecommended ? <ShieldCheck className="w-5 h-5 text-primary" /> : <XCircle className="w-5 h-5 text-destructive" />}
                      <span className={`text-xs font-semibold uppercase tracking-wider ${item.isRecommended ? "text-secondary-foreground" : "text-muted-foreground"}`}>
                        {item.isRecommended ? "Recommended Local Method" : "Off-the-Shelf Standard"}
                      </span>
                    </div>
                  </div>

                  <h4 className="text-lg sm:text-xl font-bold text-foreground mb-1">
                    {item.approach}
                  </h4>
                  <p className="text-xs sm:text-sm text-muted-foreground mb-6">
                    {item.subtitle}
                  </p>

                  <ul className="space-y-3.5 mb-6">
                    {item.points.map((point, pIndex) => <li key={pIndex} data-index={pIndex} className="flex items-start gap-3 text-xs sm:text-sm leading-relaxed">
                        {item.isRecommended ? <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />}
                        <span className={item.isRecommended ? "text-foreground font-normal" : "text-muted-foreground"}>
                          {point}
                        </span>
                      </li>)}
                  </ul>
                </div>

                {item.isRecommended && <div className="pt-4 border-t border-border flex items-center justify-between flex-wrap gap-4">
                    <div className="text-xs text-muted-foreground">
                      Safe formulations suited for Auckland households, pets, and native garden borders.
                    </div>
                  </div>}
              </div>)}
          </div>
        </div>

        {/* Narrative Proof Banner with Local Quote Action */}
        <div className="rounded-lg bg-card border border-border p-6 sm:p-8 lg:p-10 shadow-xs">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="h-0.5 w-8 bg-primary mb-3" />
              <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-3">
                Stop the cycle before pests establish their breeding nest
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-4">
                Whether you have heard scratching sounds in your ceiling or noticed ants along window sills, our experienced Auckland team carries out detailed inspections and delivers safe, long-lasting barrier care.
              </p>
              <div className="flex flex-wrap items-center gap-6 text-xs sm:text-sm text-foreground">
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary" />
                  Free, transparent Auckland quotes
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary" />
                  Eco-friendly, biodegradable treatments
                </span>
              </div>
            </div>

            <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-3 justify-end items-stretch sm:items-center lg:items-stretch xl:items-center">
              <Button type="button" onClick={() => {
              if (typeof window !== "undefined") {
                window.dispatchEvent(new CustomEvent("eco-open-quote-modal"));
              }
            }} className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold tracking-wide uppercase px-6 py-3 rounded-md text-xs sm:text-sm shadow-xs">
                <span>Get a Free Quote</span>
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>

              <Button asChild variant="secondary" className="bg-secondary text-secondary-foreground border border-border hover:bg-accent font-semibold px-5 py-3 rounded-md text-xs sm:text-sm">
                <a href="tel:0221002103">
                  <Phone className="w-4 h-4 mr-1 text-primary" />
                  <span>Call 022 100 2103</span>
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}